import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  LogOut,
  QrCode,
  Users,
  Check,
  UserRoundCheck,
  X,
  Camera,
} from "lucide-react";
import { format } from "date-fns";

const fmt = (d) => (d ? format(new Date(d), "PPp") : "—");

const CheckInPage = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [qr, setQr] = useState(null); // { title, url, qr_png }
  const [verify, setVerify] = useState(null); // active verification modal
  const [notes, setNotes] = useState("");

  const loadEvents = async () => {
    try {
      const { data } = await api.get("/admin/checkin/events");
      setEvents(data);
      if (!selected && data.length) setSelected(data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load events");
    }
  };

  const loadDetail = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/checkin/events/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Failed to load event detail");
    }
  };

  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadDetail(selected); /* eslint-disable-next-line */ }, [selected]);

  const showPersonalQr = async (bookingId, label) => {
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/invite`);
      setQr({ title: `Personal invite · ${label}`, url: data.url, qr_png: data.qr_png });
    } catch (e) {
      toast.error("Could not create invite");
    }
  };

  const showGenericQr = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/admin/events/${selected}/generic-invite`, { max_uses: 500, expires_hours: 72 });
      setQr({ title: "Walk-up QR (post at door)", url: data.url, qr_png: data.qr_png });
    } catch (e) {
      toast.error("Could not create QR");
    }
  };

  const openVerify = (booking, parent, child) => {
    setVerify({ booking, parent, child });
    setNotes("");
  };

  const markCheck = async (action) => {
    if (!verify) return;
    try {
      await api.post("/admin/checkin/mark", {
        booking_id: verify.booking.id,
        child_id: verify.child.id,
        action,
        notes,
      });
      toast.success(`${verify.child.first_name} checked ${action}.`);
      setVerify(null);
      await loadDetail(selected);
      await loadEvents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not mark check-in");
    }
  };

  return (
    <main data-testid="checkin-page" className="min-h-screen bg-muted/40">
      <div className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <span className="text-foreground/30">·</span>
            <span className="font-serif-display text-lg">Check-In Kiosk</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/admin/login"); }} className="rounded-full" data-testid="checkin-logout">
              <LogOut size={14} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar — events */}
        <aside data-testid="checkin-events" className="rounded-2xl border border-border/70 bg-card p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground px-2 pb-2">Today · Upcoming</div>
          <div className="space-y-1">
            {events.map((e) => (
              <button
                key={e.id}
                data-testid={`checkin-event-${e.slug}`}
                onClick={() => setSelected(e.id)}
                className={`w-full text-left rounded-xl px-3 py-3 transition ${selected === e.id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted"}`}
              >
                <div className="text-sm font-medium text-foreground">{e.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{fmt(e.start_at)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  <Badge variant="secondary" className="mr-1">{e.booking_count} bookings</Badge>
                  <Badge className="bg-primary/15 text-primary">{e.checked_in_count} in</Badge>
                </div>
              </button>
            ))}
            {events.length === 0 && <div className="p-3 text-xs text-muted-foreground">No upcoming events.</div>}
          </div>
        </aside>

        {/* Main — bookings list */}
        <section data-testid="checkin-detail" className="rounded-2xl border border-border/70 bg-card p-6">
          {detail && (
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
                <div>
                  <h1 className="font-serif-display text-2xl">{detail.event.name}</h1>
                  <div className="text-xs text-muted-foreground mt-1">
                    {detail.event.venue} · {fmt(detail.event.start_at)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={showGenericQr} data-testid="btn-generic-qr" className="rounded-full">
                    <QrCode size={14} className="mr-1.5" /> Walk-up QR
                  </Button>
                </div>
              </div>

              {detail.bookings.length === 0 && (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No bookings yet. Post the walk-up QR at the door so parents can pre-check-in on arrival.
                </div>
              )}

              <div className="space-y-4">
                {detail.bookings.map(({ booking, parent, children }) => (
                  <div key={booking.id} data-testid={`booking-row-${booking.id}`} className="rounded-2xl border border-border/70 bg-background p-5">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        {parent?.photo_url ? (
                          <img
                            src={parent.photo_url}
                            alt="parent"
                            className="h-14 w-14 rounded-full object-cover border border-border shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <div className="h-14 w-14 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
                            <Camera size={16} />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-foreground">
                            {parent ? `${parent.first_name} ${parent.last_name}` : <span className="text-muted-foreground italic">Unassigned booking</span>}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{parent?.phone} · {parent?.email}</div>
                          {parent?.notes && <div className="mt-2 text-xs text-muted-foreground italic max-w-md">"{parent.notes}"</div>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {parent && (
                          <Button variant="outline" size="sm" onClick={() => showPersonalQr(booking.id, `${parent.first_name} ${parent.last_name}`)} data-testid={`btn-personal-qr-${booking.id}`} className="rounded-full">
                            <QrCode size={12} className="mr-1.5" /> Invite QR
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {children.map((c) => {
                        const isIn = c.last_check?.action === "in";
                        return (
                          <div key={c.id} data-testid={`child-row-${c.id}`} className="rounded-xl border border-border/60 p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              {c.photo_url ? (
                                <img src={c.photo_url} alt={c.first_name} className="h-12 w-12 rounded-full object-cover border border-border shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                              ) : (
                                <div className="h-12 w-12 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0"><Camera size={14} /></div>
                              )}
                              <div>
                                <div className="font-medium text-foreground text-sm">{c.first_name}</div>
                                <div className="text-xs text-muted-foreground">Age {c.age_years}</div>
                                {c.allergies && <div className="text-xs mt-1 text-destructive">Allergies: {c.allergies}</div>}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {c.last_check && (
                                <Badge variant={isIn ? "default" : "secondary"} className={isIn ? "bg-primary/15 text-primary" : ""}>
                                  {isIn ? "Checked in" : "Checked out"} · {fmt(c.last_check.at)}
                                </Badge>
                              )}
                              <Button size="sm" onClick={() => openVerify(booking, parent, c)} data-testid={`btn-verify-${c.id}`} className="rounded-full bg-primary text-primary-foreground">
                                <UserRoundCheck size={12} className="mr-1.5" /> Verify
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {children.length === 0 && (
                        <div className="text-xs text-muted-foreground italic col-span-2">
                          No children on file yet — parent hasn't completed their care profile.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* QR modal */}
      <Dialog open={!!qr} onOpenChange={(v) => !v && setQr(null)}>
        <DialogContent className="max-w-md" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display">{qr?.title}</DialogTitle>
            <DialogDescription>Scan or share the link to invite a parent to pre-register.</DialogDescription>
          </DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-4">
              <img src={qr.qr_png} alt="QR code" className="w-64 h-64 rounded-xl border border-border" data-testid="qr-image" />
              <div className="text-xs text-muted-foreground break-all text-center">
                <a href={qr.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{qr.url}</a>
              </div>
              <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(qr.url); toast.success("Link copied."); }} className="rounded-full">
                Copy link
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify (side-by-side) modal */}
      <Dialog open={!!verify} onOpenChange={(v) => !v && setVerify(null)}>
        <DialogContent className="max-w-2xl" data-testid="verify-dialog">
          {verify && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-display">Verify identity · {verify.child?.first_name}</DialogTitle>
                <DialogDescription>
                  Confirm the person in front of you matches BOTH photos on file. Match parent and child before checking in.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <PhotoCard label="Parent on file" name={`${verify.parent?.first_name || ""} ${verify.parent?.last_name || ""}`.trim() || "—"} url={verify.parent?.photo_url} />
                <PhotoCard label="Child on file" name={verify.child?.first_name || "—"} url={verify.child?.photo_url} />
              </div>
              {(!verify.parent?.photo_url || !verify.child?.photo_url) && (
                <div className="rounded-lg bg-destructive/10 text-destructive text-xs p-3">
                  ⚠️ One or both photos missing. Ask the parent to complete their profile before proceeding.
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Notes (optional)</label>
                <Textarea
                  data-testid="verify-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Parent showed driver's license as secondary ID."
                  rows={2}
                  className="mt-1 rounded-lg resize-none"
                />
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => markCheck("out")} data-testid="btn-check-out" className="rounded-full">
                  <X size={14} className="mr-1.5" /> Check out
                </Button>
                <Button onClick={() => markCheck("in")} data-testid="btn-check-in" className="rounded-full bg-primary text-primary-foreground">
                  <Check size={14} className="mr-1.5" /> Verified · check in
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

const PhotoCard = ({ label, name, url }) => (
  <div className="rounded-xl border border-border p-3 bg-background">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{label}</div>
    {url ? (
      <img src={url} alt={name} className="w-full h-56 object-cover rounded-lg" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    ) : (
      <div className="w-full h-56 rounded-lg border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground gap-2">
        <Camera size={22} />
        <span className="text-xs">No photo on file</span>
      </div>
    )}
    <div className="mt-2 text-sm font-medium text-foreground text-center">{name}</div>
  </div>
);

export default CheckInPage;
